/**
 * Contact Details tab for Profile Event Essentials.
 * Primary (email/phone with OTP) and secondary contact.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Edit2, Mail, Phone, CheckCircle2 } from 'lucide-react';
import { Input } from '../../../components/ui/Input';
import { Button } from '../../../components/ui/Button';
import { Sheet } from '../../../components/ui/Sheet';
import toast from 'react-hot-toast';
import { ArtistService } from '../../../services/artistService';
import { AuthService, getAuthErrorMessage } from '../../../services/authService';
import { useContactDetailsStore } from '../../../stores/contactDetailsStore';
import { Loader, OTPInput, PhoneInput } from '@/components/ui';
import { INDIAN_PHONE_ERROR_MESSAGE, isValidIndianPhoneNumber } from '../../../utils/phone';

export const ContactDetailsTab: React.FC = () => {
    const contactDetails = useContactDetailsStore((s) => s.data);
    const isLoadingStore = useContactDetailsStore((s) => s.isLoading);
    const ensureFetched = useContactDetailsStore((s) => s.ensureFetched);
    const fetchContactDetails = useContactDetailsStore((s) => s.fetchContactDetails);

    const [contactDetailsLoading, setContactDetailsLoading] = useState(false);
    const [otpSentForEmail, setOtpSentForEmail] = useState(false);
    const [otpSentForPhone, setOtpSentForPhone] = useState(false);
    const [emailOtp, setEmailOtp] = useState('');
    const [phoneOtp, setPhoneOtp] = useState('');
    const [sendingEmailOtp, setSendingEmailOtp] = useState(false);
    const [sendingPhoneOtp, setSendingPhoneOtp] = useState(false);
    const [verifyingEmail, setVerifyingEmail] = useState(false);
    const [verifyingPhone, setVerifyingPhone] = useState(false);

    const [modifySheetOpen, setModifySheetOpen] = useState(false);
    const [modifyType, setModifyType] = useState<'email' | 'phone'>('email');
    const [modifyValue, setModifyValue] = useState('');
    const [modifyLoading, setModifyLoading] = useState(false);

    const [secondaryModifyMode, setSecondaryModifyMode] = useState(false);
    const [secondaryEmailEdit, setSecondaryEmailEdit] = useState('');
    const [secondaryPhoneEdit, setSecondaryPhoneEdit] = useState('');
    const [secondarySaveLoading, setSecondarySaveLoading] = useState(false);

    const {
        primary,
        secondary: secondaryList,
        contact_details_update_requests: updateRequests = [],
    } = contactDetails ?? {};

    const latestUpdateRequest = useMemo(() => {
        if (!updateRequests || updateRequests.length === 0) return undefined;
        // Sort by created_at desc to pick most recent request when multiple exist
        const sorted = [...updateRequests].sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        return sorted[0];
    }, [updateRequests]);

    // Always show primary (old) email/phone in the section; requested values only in the "Request under review" toast
    const displayEmail = primary?.email ?? '—';
    const displayPhone = primary?.phone ?? '—';

    const firstSecondary = secondaryList && secondaryList.length > 0 ? secondaryList[0] : undefined;

    useEffect(() => {
        setContactDetailsLoading(true);
        ensureFetched().finally(() => setContactDetailsLoading(false));
    }, [ensureFetched]);

    useEffect(() => {
        if (firstSecondary) {
            setSecondaryEmailEdit(firstSecondary.email ?? '');
            setSecondaryPhoneEdit(firstSecondary.phone ?? '');
        }
    }, [firstSecondary?.email, firstSecondary?.phone]);

    const handleSendEmailOtp = async () => {
        const target = primary?.email?.trim();
        if (!target) {
            toast.error('Primary email not available');
            return;
        }
        setSendingEmailOtp(true);
        try {
            await AuthService.sendProfileVerifyOtp(target);
            setOtpSentForEmail(true);
            setEmailOtp('');
            toast.success('OTP sent successfully');
        } catch (error) {
            toast.error(getAuthErrorMessage(error, 'Failed to send OTP. Please try again.'));
        } finally {
            setSendingEmailOtp(false);
        }
    };

    const handleVerifyEmailOtp = async () => {
        if (!emailOtp || emailOtp.length !== 4) return;
        const target = primary?.email?.trim();
        if (!target) {
            toast.error('Primary email not available');
            return;
        }
        setVerifyingEmail(true);
        try {
            await AuthService.verifyProfileOtp(target, emailOtp);
            toast.success('Email verified');
            setOtpSentForEmail(false);
            setEmailOtp('');
            await fetchContactDetails();
        } catch {
            toast.error('Invalid or expired OTP');
        } finally {
            setVerifyingEmail(false);
        }
    };

    const handleSendPhoneOtp = async () => {
        const target = primary?.phone?.trim();
        if (!target) {
            toast.error('Primary phone not available');
            return;
        }
        setSendingPhoneOtp(true);
        try {
            await AuthService.sendProfileVerifyOtp(target);
            setOtpSentForPhone(true);
            setPhoneOtp('');
            toast.success('OTP sent successfully');
        } catch (error) {
            toast.error(getAuthErrorMessage(error, 'Failed to send OTP. Please try again.'));
        } finally {
            setSendingPhoneOtp(false);
        }
    };

    const handleVerifyPhoneOtp = async () => {
        if (!phoneOtp || phoneOtp.length !== 4) return;
        const target = primary?.phone?.trim();
        if (!target) {
            toast.error('Primary phone not available');
            return;
        }
        setVerifyingPhone(true);
        try {
            await AuthService.verifyProfileOtp(target, phoneOtp);
            toast.success('Phone verified');
            setOtpSentForPhone(false);
            setPhoneOtp('');
            await fetchContactDetails();
        } catch {
            toast.error('Invalid or expired OTP');
        } finally {
            setVerifyingPhone(false);
        }
    };

    const openModifySheet = (type: 'email' | 'phone') => {
        setModifyType(type);
        setModifyValue('');
        setModifySheetOpen(true);
    };

    const handleSendModifyRequest = async () => {
        const trimmedRaw = modifyValue.trim();
        if (!trimmedRaw) return;

        // Normalise based on type
        const primaryEmail = primary?.email?.trim().toLowerCase() || '';
        const primaryPhone = primary?.phone?.replace(/\s+/g, '').trim() || '';
        const secondaryEmail = firstSecondary?.email?.trim().toLowerCase() || '';
        const secondaryPhone = firstSecondary?.phone?.replace(/\s+/g, '').trim() || '';

        if (modifyType === 'email') {
            const trimmedEmail = trimmedRaw.toLowerCase();

            // Requested primary email cannot be same as secondary email
            if (secondaryEmail && trimmedEmail === secondaryEmail) {
                toast.error('Primary email cannot be same as secondary email');
                return;
            }

            // Keep original spacing for API but validated email string
            setModifyLoading(true);
            try {
                await ArtistService.requestContactEmailModification(trimmedRaw);
                toast.success('Email modification request submitted');
                setModifySheetOpen(false);
                setModifyValue('');
                await fetchContactDetails();
            } catch {
                toast.error('Failed to submit request');
            } finally {
                setModifyLoading(false);
            }
        } else {
            // Phone flow
            const trimmed = trimmedRaw.replace(/\s+/g, '').trim();
            if (!trimmed) return;
            if (!isValidIndianPhoneNumber(trimmed)) {
                toast.error(INDIAN_PHONE_ERROR_MESSAGE);
                return;
            }

            // Requested primary phone cannot be same as secondary phone
            if (secondaryPhone && trimmed === secondaryPhone) {
                toast.error('Primary phone cannot be same as secondary phone');
                return;
            }

            setModifyLoading(true);
            try {
                await ArtistService.requestContactPhoneModification(trimmed);
                toast.success('Phone modification request submitted');
                setModifySheetOpen(false);
                setModifyValue('');
                await fetchContactDetails();
            } catch {
                toast.error('Failed to submit request');
            } finally {
                setModifyLoading(false);
            }
        }
    };

    const handleSecondarySave = async () => {
        const secondaryEmailTrimmed = secondaryEmailEdit.trim();
        const trimmedPhone = secondaryPhoneEdit.replace(/\s+/g, '').trim();

        if (trimmedPhone && !isValidIndianPhoneNumber(trimmedPhone)) {
            toast.error(INDIAN_PHONE_ERROR_MESSAGE);
            return;
        }

        const primaryEmail = primary?.email?.trim().toLowerCase() || '';
        const primaryPhone = primary?.phone?.replace(/\s+/g, '').trim() || '';
        const latestRequestedEmail = latestUpdateRequest?.email?.trim().toLowerCase() || '';
        const latestRequestedPhone =
            latestUpdateRequest?.phone?.replace(/\s+/g, '').trim() || '';

        // Secondary email cannot be same as primary or requested primary email
        if (
            secondaryEmailTrimmed &&
            ((primaryEmail && secondaryEmailTrimmed.toLowerCase() === primaryEmail) ||
                (latestRequestedEmail &&
                    secondaryEmailTrimmed.toLowerCase() === latestRequestedEmail))
        ) {
            toast.error('Secondary email cannot be same as primary email');
            return;
        }

        // Secondary phone cannot be same as primary or requested primary phone
        if (
            trimmedPhone &&
            ((primaryPhone && trimmedPhone === primaryPhone) ||
                (latestRequestedPhone && trimmedPhone === latestRequestedPhone))
        ) {
            toast.error('Secondary phone cannot be same as primary phone');
            return;
        }

        setSecondarySaveLoading(true);
        try {
            await ArtistService.updateContactDetailsSecondary({
                email: secondaryEmailTrimmed || null,
                mobile: trimmedPhone || null,
            });
            toast.success('Secondary contact updated');
            setSecondaryModifyMode(false);
            await fetchContactDetails();
        } catch {
            toast.error('Failed to update secondary contact');
        } finally {
            setSecondarySaveLoading(false);
        }
    };

    return (
        <>
            <motion.div
                key="secondary"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-8"
            >
                {(contactDetailsLoading || isLoadingStore) ? (
                    <div className="flex justify-center py-12">
                        <Loader size="small" />
                    </div>
                ) : (
                    <>
                        <section className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] p-5 lg:p-6 space-y-5">
                            <div>
                                <h2 className="text-base lg:text-lg font-semibold text-white flex items-center gap-2">
                                    <Mail className="h-5 w-5 text-[#F33363]" />
                                    Primary contact
                                </h2>
                                <p className="text-sm text-[#8E8E93] mt-1">Email and phone with verification. Use Modify next to each field to request a change.</p>
                            </div>
                            <div className="space-y-4 pt-2 border-t border-[#3A3A3C]">
                                <div className="rounded-xl bg-[#151517] border border-[#3A3A3C] p-4 space-y-2">
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                        <span className="text-[10px] font-black text-[#5A5A5E] uppercase tracking-widest">Email</span>
                                        <div className="flex items-center gap-2">
                                            {primary?.update_request?.email && (
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        toast(
                                                            `Your email modification request is currently under review.${latestUpdateRequest?.email ? ` Requested: ${latestUpdateRequest.email}` : ''}`,
                                                            { duration: 4000 }
                                                        )
                                                    }
                                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-500 text-[10px] font-medium"
                                                >
                                                    Request under review
                                                </button>
                                            )}
                                            {!primary?.update_request?.email && (
                                                <button
                                                    type="button"
                                                    onClick={() => openModifySheet('email')}
                                                    className="inline-flex items-center gap-1 rounded-full bg-[#151517] border border-[#3A3A3C] px-2.5 py-1 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors"
                                                >
                                                    <Edit2 className="h-3 w-3" /> Modify
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <p className="text-sm font-medium text-white">{displayEmail}</p>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {primary?.email_verified ? (
                                            <span className="inline-flex items-center gap-1 text-emerald-500 text-xs font-medium">
                                                <CheckCircle2 className="h-3.5 w-3.5" /> Verified
                                            </span>
                                        ) : (
                                            <>
                                                {!otpSentForEmail ? (
                                                    <button
                                                        type="button"
                                                        onClick={handleSendEmailOtp}
                                                        disabled={sendingEmailOtp}
                                                        className="relative inline-flex items-center gap-1 rounded-full bg-[#2C2C2E] border border-[#3A3A3C] px-2.5 py-1 text-[11px] text-[#8E8E93] hover:text-white hover:bg-[#3A3A3C] transition-colors"
                                                    >
                                                        {sendingEmailOtp && (
                                                            <span className="absolute inset-0 flex items-center justify-center">
                                                                <Loader size="small" />
                                                            </span>
                                                        )}
                                                        <span className={sendingEmailOtp ? 'opacity-0' : ''}>
                                                            Verify Email
                                                        </span>
                                                    </button>
                                                ) : (
                                                    <div className="space-y-2">
                                                        <OTPInput length={4} value={emailOtp} onChange={setEmailOtp} onComplete={setEmailOtp} className="flex gap-2" />
                                                        <div className="flex items-center gap-2">
                                                            <Button
                                                                type="button"
                                                                variant="active"
                                                                onClick={handleVerifyEmailOtp}
                                                                disabled={emailOtp.length !== 4}
                                                                isLoading={verifyingEmail}
                                                                className="px-3 py-1.5 text-xs"
                                                            >
                                                                Verify
                                                            </Button>
                                                            <button
                                                                type="button"
                                                                onClick={() => { setOtpSentForEmail(false); setEmailOtp(''); }}
                                                                className="inline-flex items-center rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#8E8E93] hover:text-white hover:bg-[#2C2C2E] transition-colors"
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="rounded-xl bg-[#151517] border border-[#3A3A3C] p-4 space-y-2">
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                        <span className="text-[10px] font-black text-[#5A5A5E] uppercase tracking-widest">Phone</span>
                                        <div className="flex items-center gap-2">
                                            {primary?.update_request?.phone && (
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        toast(
                                                            `Your phone modification request is currently under review.${latestUpdateRequest?.phone ? ` Requested: ${latestUpdateRequest.phone}` : ''}`,
                                                            { duration: 4000 }
                                                        )
                                                    }
                                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-500 text-[10px] font-medium"
                                                >
                                                    Request under review
                                                </button>
                                            )}
                                            {!primary?.update_request?.phone && (
                                                <button
                                                    type="button"
                                                    onClick={() => openModifySheet('phone')}
                                                    className="inline-flex items-center gap-1 rounded-full bg-[#151517] border border-[#3A3A3C] px-2.5 py-1 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors"
                                                >
                                                    <Edit2 className="h-3 w-3" /> Modify
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <p className="text-sm font-medium text-white">{displayPhone}</p>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {primary?.phone_verified ? (
                                            <span className="inline-flex items-center gap-1 text-emerald-500 text-xs font-medium">
                                                <CheckCircle2 className="h-3.5 w-3.5" /> Verified
                                            </span>
                                        ) : (
                                            <>
                                                {!otpSentForPhone ? (
                                                    <button
                                                        type="button"
                                                        onClick={handleSendPhoneOtp}
                                                        disabled={sendingPhoneOtp}
                                                        className="relative inline-flex items-center gap-1 rounded-full bg-[#2C2C2E] border border-[#3A3A3C] px-2.5 py-1 text-[11px] text-[#8E8E93] hover:text-white hover:bg-[#3A3A3C] transition-colors"
                                                    >
                                                        {sendingPhoneOtp && (
                                                            <span className="absolute inset-0 flex items-center justify-center">
                                                                <Loader size="small" />
                                                            </span>
                                                        )}
                                                        <span className={sendingPhoneOtp ? 'opacity-0' : ''}>
                                                            Verify Phone
                                                        </span>
                                                    </button>
                                                ) : (
                                                    <div className="space-y-2">
                                                        <OTPInput length={4} value={phoneOtp} onChange={setPhoneOtp} onComplete={setPhoneOtp} className="flex gap-2" />
                                                        <div className="flex items-center gap-2">
                                                            <Button
                                                                type="button"
                                                                variant="active"
                                                                onClick={handleVerifyPhoneOtp}
                                                                disabled={phoneOtp.length !== 4}
                                                                isLoading={verifyingPhone}
                                                                className="px-3 py-1.5 text-xs"
                                                            >
                                                                Verify
                                                            </Button>
                                                            <button
                                                                type="button"
                                                                onClick={() => { setOtpSentForPhone(false); setPhoneOtp(''); }}
                                                                className="inline-flex items-center rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#8E8E93] hover:text-white hover:bg-[#2C2C2E] transition-colors"
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] p-5 lg:p-6 space-y-5">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div>
                                    <h2 className="text-base lg:text-lg font-semibold text-white flex items-center gap-2">
                                        <Phone className="h-5 w-5 text-[#F33363]" />
                                        Secondary contact
                                    </h2>
                                    <p className="text-sm text-[#8E8E93] mt-1">Backup email and phone</p>
                                </div>
                                {!secondaryModifyMode ? (
                                    <button type="button" onClick={() => setSecondaryModifyMode(true)} className="inline-flex items-center gap-1.5 rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors text-nowrap">
                                        <Edit2 className="h-3.5 w-3.5" />
                                        Modify
                                    </button>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <button type="button" onClick={() => setSecondaryModifyMode(false)} className="inline-flex items-center rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors">
                                            Cancel
                                        </button>
                                        <Button
                                            type="button"
                                            variant="active"
                                            className="px-3 py-1.5 text-xs font-medium"
                                            onClick={handleSecondarySave}
                                            disabled={secondarySaveLoading}
                                            isLoading={secondarySaveLoading}
                                        >
                                            Save
                                        </Button>
                                    </div>
                                )}
                            </div>
                            <div className="space-y-4 pt-2 border-t border-[#3A3A3C]">
                                {!secondaryModifyMode ? (
                                    <>
                                        <div>
                                            <p className="text-[10px] font-black text-[#5A5A5E] uppercase tracking-widest mb-1">Email</p>
                                            <p className="text-sm font-medium text-white">{firstSecondary?.email || '—'}</p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] font-black text-[#5A5A5E] uppercase tracking-widest mb-1">Phone</p>
                                            <p className="text-sm font-medium text-white">
                                                {firstSecondary?.phone
                                                    ? `${firstSecondary.phone.slice(0, 5)} ${firstSecondary.phone.slice(5)}`
                                                    : '—'}
                                            </p>
                                        </div>
                                    </>
                                ) : (
                                    <div className="space-y-4">
                                        <Input
                                            label="Secondary email"
                                            value={secondaryEmailEdit}
                                            onChange={(e) => setSecondaryEmailEdit(e.target.value)}
                                            placeholder="Backup email"
                                        />
                                        <PhoneInput
                                            label="Secondary phone"
                                            value={secondaryPhoneEdit}
                                            onChange={(value) => setSecondaryPhoneEdit(value)}
                                            placeholder="Enter 10-digit phone"
                                            maxLength={11}
                                        />
                                    </div>
                                )}
                            </div>
                        </section>
                    </>
                )}
            </motion.div>

            <Sheet
                open={modifySheetOpen}
                onOpenChange={setModifySheetOpen}
                title={modifyType === 'email' ? 'Change email' : 'Change phone'}
                description={modifyType === 'email' ? `Current: ${primary?.email ?? '—'}` : `Current: ${primary?.phone ?? '—'}`}
            >
                <div className="space-y-4 pt-2">
                    {modifyType === 'email' ? (
                        <Input
                            label="New email"
                            value={modifyValue}
                            onChange={(e) => setModifyValue(e.target.value)}
                            placeholder="Enter new email"
                        />
                    ) : (
                        <PhoneInput
                            label="New phone"
                            value={modifyValue}
                            onChange={(value) => setModifyValue(value)}
                            placeholder="Enter 10-digit phone"
                            maxLength={11}
                        />
                    )}
                    <Button
                        type="button"
                        variant="active"
                        className="w-full uppercase tracking-widest"
                        onClick={handleSendModifyRequest}
                        disabled={!modifyValue.trim()}
                        isLoading={modifyLoading}
                    >
                        Send modification request
                    </Button>
                </div>
            </Sheet>
        </>
    );
};
